import { ConversationMessage, QuickButton, SessionContext } from '../conversation/types';
import { ConversationService } from '../conversation/conversationService';
import { CaseDetailsService } from '../caseDetails/caseDetailsService';
import { CaseSummary } from '../caseDetails/caseSummary';
import { TriagePayload, TriageService } from '../triage/types';
import { ConversationTicket } from '../conversation/types';
import { TicketService } from '../ticket/types';
import { buildTicketPayload, noopTicketService, withTicketAuth } from '../ticket/ticketService';
import { LlmService } from '../llm/types';
import { CaseMessageService, noopCaseMessageService } from '../caseMessages/types';
import { buildLlmPayload, MAX_RECENT_CASES, noopLlmService } from '../llm/llmService';
import { extractCaseNumber, matchIntent } from '../presets/intentMatcher';
import { buttonFor, CASE_STATUS_PRESET_ID, CLEAR_CONTACT_FLOW, CONTACT_SUPPORT_PRESET_ID, END_CHAT_PRESET_ID, errorTurn, escalationOfferTurn, fallbackTurn, greetingTurn, handOffToSupport, isHandoffFollowUp, menuButtons, presetButton, PRESETS, unresolvedTurn, WHO_TO_CONTACT_PRESET_ID } from '../presets/catalog';
import { calmingAck, calmingDeflect, EMERGENCY_REPLY, escalationAck } from '../presets/responses';
import { personalityById, pickPersonality } from '../presets/personalities';
import { caseChatLink, caseQuickButtons, MAX_CASE_PICKS } from '../caseDetails/formatCase';
import { detectSentiment, Sentiment } from './sentiment';
import { isUrgent } from './urgency';
import { BotTurn, CASE_BUTTON_PREFIX, CASE_LOOKUP_PRESET_ID, EMERGENCY_INTENT_ID, LLM_INTENT_ID, Preset, PresetContext, PresetInput, PresetUser, PRESET_BUTTON_PREFIX, selectedCaseFrom } from '../presets/types';

export interface EngineResult {
  /** Messages added to the session by this call, in order. */
  newMessages: ConversationMessage[];
  /** Quick buttons to show after the last message. */
  quickButtons: QuickButton[];
  /** The user asked to end the chat: the widget should collect feedback, archive the session and close. */
  endChat?: boolean;
}

export interface ChatEngine {
  /** Loads the session; greets when it is empty. Returns the whole history plus current buttons. */
  start(): Promise<{ messages: ConversationMessage[]; quickButtons: QuickButton[] }>;
  handleUserText(text: string): Promise<EngineResult>;
  handleQuickButton(value: string): Promise<EngineResult>;
  newSession(feedback?: { resolved: boolean; score: number; comment: string }): Promise<{ messages: ConversationMessage[]; quickButtons: QuickButton[] }>;
  rateMessage(messageId: string, rating: 'liked' | 'disliked'): Promise<void>;
  /** The user-bubble text that `handleQuickButton(value)` will record, for optimistic rendering. */
  quickButtonText(value: string): string;
  /** Resolves once every queued ticket start/sync has finished. Ticket work never rejects. */
  flushTicket(): Promise<void>;
}

export interface ChatEngineDeps {
  conversation: ConversationService;
  caseDetails: CaseDetailsService;
  triage: TriageService;
  /** Answers messages no preset matches. Defaults to a no-op, which keeps the canned fallback. */
  llm?: LlmService;
  /** Keeps a Jira ticket in step with the conversation. Defaults to a no-op (no ticket on the session). */
  ticket?: TicketService;
  /** Posts to a case's Messages tab. Defaults to a no-op that reports every post as failed. */
  caseMessages?: CaseMessageService;
  user: PresetUser;
  presets?: Preset[];
  logger?: Pick<Console, 'warn'>;
  /** Random source for the voice drawn when a conversation starts. Injectable so tests can force one. */
  pickPersonality?: () => number;
}

/** After the ticket API refused to start a ticket, wait this long before trying again on a later turn. */
export const TICKET_RETRY_MS = 5 * 60 * 1000;

/** Inputs not worth seeding a support ticket with. */
const THROWAWAY_INPUT = /^(ok|okay|k|kk|no|nope|yes|yep|yeah|hmm|hm|oh|ah|\?+|\.+)$/i;

/** Puts an acknowledgement in its own bubble before the substantive reply. */
const withLeadIn = (turn: BotTurn, lead: string): BotTurn => ({ ...turn, messages: [lead, ...turn.messages] });

const withContext = (turn: BotTurn, extra: Partial<SessionContext>): BotTurn => ({ ...turn, context: { ...(turn.context || {}), ...extra } });

const CALM: Sentiment = { frustrated: false, demandsHuman: false };

const lastQuickButtons = (messages: ConversationMessage[]): QuickButton[] => {
  const last = messages[messages.length - 1];
  return last && last.role === 'bot' && last.quickButtons ? last.quickButtons : [];
};

export function createChatEngine({
  conversation,
  caseDetails,
  triage,
  llm = noopLlmService,
  ticket = noopTicketService,
  caseMessages = noopCaseMessageService,
  user,
  presets = PRESETS,
  logger = console,
  pickPersonality: pickVoice = Math.random,
}: ChatEngineDeps): ChatEngine {
  /**
   * Ticket work runs off the turn's critical path, one call at a time and in order, so a reply is never
   * held up by Jira and a later sync never overtakes an earlier one.
   */
  let ticketQueue: Promise<void> = Promise.resolve();
  const enqueueTicketWork = (work: () => Promise<void>): Promise<void> => {
    ticketQueue = ticketQueue.then(work).catch((error) => logger.warn('[askAi] ticket update failed', error));
    return ticketQueue;
  };

  const ticketPayload = () => {
    const session = conversation.getSession();
    return buildTicketPayload({
      session,
      user,
      selectedCase: selectedCaseFrom(session.context),
      sentiment: session.context.escalationPriority === 'high' ? 'frustrated' : undefined,
    });
  };

  /** Starts the ticket on the first user message, then rewrites its transcript after every later turn. */
  const syncTicket = async (force = false): Promise<void> => {
    const session = conversation.getSession();
    const current = session.ticket;
    if (!current) {
      if (session.ticketFailedAt && Date.now() - session.ticketFailedAt < TICKET_RETRY_MS) return;
      if (!session.messages.some((m) => m.role === 'user')) return;
      const result = await ticket.start(ticketPayload());
      if (result.mode === 'noop') return;
      const live = conversation.getSession();
      if (live.id !== session.id) return; // the session ended while the request was in flight
      if (!result.accepted || !result.ticketRef) {
        await conversation.setTicket(undefined, Date.now());
        return;
      }
      await conversation.setTicket({
        ref: result.ticketRef,
        url: result.ticketUrl,
        token: result.ticketToken,
        state: 'in_progress',
        syncedCount: live.messages.length,
      });
      // Messages may have arrived while the ticket was being created; a sync picks them up.
      if (live.messages.length > session.messages.length) await syncTicket();
      return;
    }
    if (current.state === 'closed') return;
    if (!force && current.syncedCount >= session.messages.length) return;
    const result = await ticket.sync(current.ref, withTicketAuth(ticketPayload(), current.token, current.escalation));
    if (!result.accepted) return;
    const live = conversation.getSession();
    if (live.id !== session.id || !live.ticket || live.ticket.ref !== current.ref) return;
    await conversation.setTicket({ ...live.ticket, syncedCount: session.messages.length });
  };

  const scheduleTicketSync = (force = false) => { void enqueueTicketWork(() => syncTicket(force)); };

  /** "Nothing else" / end chat: final transcript, feedback, then the ticket is marked Done. Never throws. */
  const closeTicket = async (feedback?: { resolved: boolean; score: number; comment: string }): Promise<void> => {
    await enqueueTicketWork(async () => {
      const session = conversation.getSession();
      const current = session.ticket;
      if (!current || current.state === 'closed') return;
      const payload = { ...withTicketAuth(ticketPayload(), current.token, current.escalation), reason: 'ended' as const, ...(feedback ? { feedback } : {}) };
      const result = await ticket.close(current.ref, payload);
      if (!result.accepted) return;
      const live = conversation.getSession();
      if (live.id !== session.id || !live.ticket) return;
      // An escalated ticket stays Open for the person handling it; everything else is now closed.
      const state: ConversationTicket['state'] = current.state === 'open' ? 'open' : 'closed';
      await conversation.setTicket({ ...live.ticket, state, syncedCount: session.messages.length });
    });
  };

  /** Every hand-off is remembered on the session, whatever the outcome. */
  const recordingTriage: TriageService = {
    async submit(payload: TriagePayload) {
      // Let any in-flight ticket start/sync finish so the hand-off escalates the right ticket and the two
      // description rewrites cannot cross.
      await ticketQueue;
      const withTicket: TriagePayload = conversation.getSession().ticket?.ref
        ? { ...payload, ticketRef: conversation.getSession().ticket!.ref, ticketToken: conversation.getSession().ticket!.token }
        : payload;
      const result = await triage.submit(withTicket);
      await conversation.recordTriage({
        reason: payload.reason,
        createdAt: Date.now(),
        accepted: result.accepted,
        mode: result.mode,
        ticketRef: result.ticketRef,
      });
      if (result.accepted && result.mode === 'remote' && result.ticketRef) {
        const existing = conversation.getSession().ticket;
        await conversation.setTicket({
          ref: result.ticketRef,
          url: result.ticketUrl ?? (existing?.ref === result.ticketRef ? existing.url : undefined),
          token: result.ticketToken ?? (existing?.ref === result.ticketRef ? existing.token : undefined),
          state: 'open',
          syncedCount: existing?.ref === result.ticketRef ? existing.syncedCount : 0,
          escalation: {
            reason: payload.reason,
            userMessage: payload.userMessage,
            contact: payload.contact,
            priority: payload.priority,
          },
        });
      }
      return result;
    },
  };

  const personality = () => personalityById(conversation.getSession().context.personalityId);

  const context = (): PresetContext => ({ user, session: conversation.getSession(), personality: personality(), caseDetails, triage: recordingTriage, caseMessages, presets });

  const applyTurn = async (turn: BotTurn, intentId?: string): Promise<EngineResult> => {
    const added: ConversationMessage[] = [];
    const buttons = turn.quickButtons || [];
    for (let i = 0; i < turn.messages.length; i += 1) {
      const isLast = i === turn.messages.length - 1;
      added.push(await conversation.append({
        role: 'bot',
        text: turn.messages[i],
        intentId,
        caseRefNo: turn.caseRefNo,
        quickButtons: isLast ? buttons : undefined,
      }));
    }
    if (turn.context) await conversation.setContext(turn.context as Partial<SessionContext>);
    return { newMessages: added, quickButtons: buttons };
  };

  const runPreset = async (preset: Preset, input: PresetInput): Promise<BotTurn> => {
    try {
      return await preset.handler(context(), input);
    } catch (error) {
      logger.warn(`[askAi] preset "${preset.id}" failed`, error);
      return { ...errorTurn(personality(), presets), context: { pendingIntentId: undefined } };
    }
  };

  const quickButtonText = (value: string): string => {
    const raw = String(value ?? '');
    if (raw.startsWith(CASE_BUTTON_PREFIX)) return `Case ${raw.slice(CASE_BUTTON_PREFIX.length)}`;
    const presetId = raw.startsWith(PRESET_BUTTON_PREFIX) ? raw.slice(PRESET_BUTTON_PREFIX.length) : raw;
    const preset = presets.find((p) => p.id === presetId);
    return preset ? preset.label : raw;
  };

  /**
   * Greets, then pre-loads the user's recent cases so they do not have to ask "how are my cases?".
   * Without any cases (or when the lookup fails) the greeting falls back to the plain menu.
   */
  const greet = async () => {
    // One voice per conversation: drawn here, kept in the session, and read by every later turn.
    await conversation.setContext({ personalityId: pickPersonality(pickVoice).id });
    const greeting = greetingTurn(personality(), user.firstName, presets);
    const caseStatus = presets.find((p) => p.id === CASE_STATUS_PRESET_ID);
    if (!caseStatus) return applyTurn(greeting, 'greeting');

    const hello = await applyTurn({ messages: greeting.messages.slice(0, 1) }, 'greeting');
    let cases: BotTurn | null = null;
    try {
      cases = await caseStatus.handler(context(), { text: caseStatus.label });
    } catch (error) {
      logger.warn('[askAi] could not pre-load cases for the greeting', error);
    }
    // Showing the cases unprompted is not the user asking for help, so it must not count as a help attempt.
    const rest = cases
      ? await applyTurn({ ...cases, context: { ...(cases.context || {}), helpAttempted: undefined } }, caseStatus.id)
      : await applyTurn({ ...greeting, messages: greeting.messages.slice(1) }, 'greeting');
    return { newMessages: [...hello.newMessages, ...rest.newMessages], quickButtons: rest.quickButtons };
  };

  /** The cases the model may talk about: the one in context plus the recent list, both already cached. */
  const casesForLlm = async (selectedRefNo?: string) => {
    const [selected, recent] = await Promise.all([
      selectedRefNo ? caseDetails.findByRefNo(selectedRefNo).catch(() => null) : Promise.resolve(null),
      caseDetails.listRecent(MAX_RECENT_CASES).catch(() => [] as CaseSummary[]),
    ]);
    return { selected, recent };
  };

  /**
   * No preset matched: ask the LLM. Returns null when it cannot answer, so the caller falls back.
   * When the model asks for a person (`escalate`), the turn unlocks the contact-support flow and
   * seeds the ticket with the model's hand-over note; the user still confirms before anything is filed.
   */
  const askLlm = async (userMessage: string, frustrated = false): Promise<BotTurn | null> => {
    try {
      const session = conversation.getSession();
      const payload = buildLlmPayload({
        session,
        user,
        userMessage,
        presets,
        selectedCase: selectedCaseFrom(session.context),
        sentiment: frustrated ? 'frustrated' : undefined,
        cases: await casesForLlm(session.context.selectedCaseRefNo),
      });
      const result = await llm.ask(payload);
      if (!result.accepted || !result.reply) return null;
      const turn: BotTurn = { messages: [result.reply], quickButtons: menuButtons(presets), context: { pendingIntentId: undefined, helpAttempted: true } };
      if (!result.escalate) return turn;
      const summary = result.summary || userMessage;
      const escalated: Partial<SessionContext> = { escalationUnlocked: true, escalationPriority: 'high', lastUnansweredText: summary };
      const supportButton = presets.find((p) => p.id === CONTACT_SUPPORT_PRESET_ID);
      const whoToContact = session.context.selectedCaseRefNo ? presets.find((p) => p.id === WHO_TO_CONTACT_PRESET_ID) : undefined;
      if (!user.email || !supportButton) {
        // No account email to file with: offer the hand-off and let the contact flow ask where to reply.
        return withContext(
          {
            ...turn,
            quickButtons: [
              ...(supportButton ? [presetButton(supportButton)] : []),
              ...(whoToContact ? [presetButton(whoToContact)] : []),
              ...menuButtons(presets),
            ],
          },
          escalated,
        );
      }
      // The model asked for a person: file now with the account email and say where and when they reply,
      // in its own bubble after the model's answer. Nothing more is asked of the dentist.
      await conversation.setContext(escalated);
      const handoff = await handOffToSupport(context(), { summary, reason: 'unresolved', priority: 'high' });
      return {
        ...turn,
        messages: [...turn.messages, ...handoff.messages],
        quickButtons: [
          ...(handoff.quickButtons || []),
          ...(whoToContact && !(handoff.quickButtons || []).some((b) => b.value === `${PRESET_BUTTON_PREFIX}${WHO_TO_CONTACT_PRESET_ID}`) ? [presetButton(whoToContact)] : []),
        ],
        context: { ...(turn.context || {}), ...escalated, ...(handoff.context || {}) },
      };
    } catch (error) {
      logger.warn('[askAi] llm handoff failed', error);
      return null;
    }
  };

  /** The case an emergency is about: the number they typed, else the one in context. Never a different case. */
  const emergencyCase = async (typedRefNo: string | undefined, selectedRefNo: string | undefined): Promise<CaseSummary | null> => {
    const refNo = typedRefNo || selectedRefNo;
    if (!refNo) return null;
    return caseDetails.findByRefNo(refNo).catch(() => null);
  };

  /**
   * Emergency turn: the two-options reply first, then the hand-off line from the shared support flow (which
   * files the ticket with the account email, or asks for a contact when there is none). With no case known,
   * the recent cases are offered as taps so the Messages-tab link is one step away.
   */
  const emergencyTurn = async (text: string, typedRefNo: string | undefined, before: SessionContext, streak: number): Promise<EngineResult> => {
    await conversation.setContext({ ...CLEAR_CONTACT_FLOW, escalationUnlocked: true, escalationPriority: 'high', helpAttempted: true, frustrationStreak: streak });
    const found = await emergencyCase(typedRefNo, before.selectedCaseRefNo);
    const caseContext: Partial<SessionContext> = found ? { selectedCaseRefNo: found.caseRefNo, selectedCaseId: found.id } : {};
    if (found) await conversation.setContext(caseContext);
    const handoff = await handOffToSupport(context(), { summary: text, reason: 'emergency', priority: 'high' });
    const recent = found ? [] : await caseDetails.listRecent(MAX_CASE_PICKS).catch(() => [] as CaseSummary[]);
    const turn: BotTurn = {
      messages: [EMERGENCY_REPLY(found ? { caseRefNo: found.caseRefNo, link: caseChatLink(found) } : null), ...handoff.messages],
      quickButtons: [
        ...caseQuickButtons(recent),
        ...(found ? buttonFor(presets, WHO_TO_CONTACT_PRESET_ID) : []),
        ...(handoff.quickButtons || []),
      ],
      context: { ...caseContext, ...(handoff.context || {}) },
      caseRefNo: found?.caseRefNo,
    };
    return applyTurn(turn, EMERGENCY_INTENT_ID);
  };

  const handleUserText = async (text: string): Promise<EngineResult> => {
    const trimmed = String(text ?? '').trim();
    if (!trimmed) return { newMessages: [], quickButtons: lastQuickButtons(conversation.getSession().messages) };

    // Once the ticket is filed, the contact flow only waits for a change of contact. Any other message
    // is a new question, so the flow lets go before the intent is matched.
    if (conversation.getSession().context.contactStep === 'handed_off' && !isHandoffFollowUp(trimmed)) {
      await conversation.setContext(CLEAR_CONTACT_FLOW);
    }
    const before = conversation.getSession().context;
    const match = matchIntent(trimmed, presets, before);
    const preset = match ? presets.find((p) => p.id === match.presetId) : undefined;

    const userMessage = await conversation.append({ role: 'user', text: trimmed, intentId: preset?.id, caseRefNo: match?.caseRefNo });
    const finish = (result: EngineResult): EngineResult => ({ ...result, newMessages: [userMessage, ...result.newMessages] });

    const inContactFlow = preset?.id === CONTACT_SUPPORT_PRESET_ID || before.pendingIntentId === CONTACT_SUPPORT_PRESET_ID;
    // An email, a phone number or "Call me instead" at a contact step is never sentiment or a fresh demand for a person.
    const atContactStep = before.pendingIntentId === CONTACT_SUPPORT_PRESET_ID
      && (before.contactStep === 'contact' || before.contactStep === 'handed_off' || before.contactStep === 'email' || before.contactStep === 'phone');
    const sentiment = atContactStep ? CALM : detectSentiment(trimmed);
    const streak = sentiment.frustrated ? (before.frustrationStreak ?? 0) + 1 : 0;
    const escalate = sentiment.frustrated && (streak >= 2 || sentiment.demandsHuman);
    const calmOnce = sentiment.frustrated && !escalate;
    // Two frustrated turns in a row earns the "going round in circles" copy; a first ask just gets a yes.
    const circling = streak >= 2;
    const mood: Partial<SessionContext> = {
      frustrationStreak: streak,
      ...(sentiment.frustrated ? { escalationPriority: 'high' as const } : {}),
      ...(escalate ? { escalationUnlocked: true, helpAttempted: true } : {}),
    };
    if (escalate) await conversation.setContext(mood);

    const hasCase = Boolean(before.selectedCaseRefNo);
    const supportButton = presets.find((p) => p.id === CONTACT_SUPPORT_PRESET_ID);
    const ensureSupportButton = (turn: BotTurn): BotTurn => {
      if (!supportButton) return turn;
      const buttons = turn.quickButtons || [];
      if (buttons.some((b) => b.value === `${PRESET_BUTTON_PREFIX}${CONTACT_SUPPORT_PRESET_ID}`)) return turn;
      return { ...turn, quickButtons: [...buttons, presetButton(supportButton)] };
    };

    // An emergency: before any preset or the model, the first reply gives both ways to reach a person right
    // now (the case's Messages tab, the helpline) and the ticket is filed at high priority in the same turn.
    if (!atContactStep && !inContactFlow && isUrgent(trimmed)) {
      return finish(await emergencyTurn(trimmed, match?.caseRefNo ?? extractCaseNumber(trimmed) ?? undefined, before, streak));
    }

    // Explicit demand for a person: skip straight to the contact flow, which the unlock above lets through.
    if (sentiment.demandsHuman && supportButton && !inContactFlow) {
      await conversation.setContext(CLEAR_CONTACT_FLOW);
      const turn = await runPreset(supportButton, { text: supportButton.label });
      return finish(await applyTurn(withContext(withLeadIn(turn, escalationAck(circling)), mood), supportButton.id));
    }

    if (preset) {
      let turn = await runPreset(preset, { text: trimmed, caseRefNo: inContactFlow ? undefined : match?.caseRefNo });
      if (!inContactFlow) turn = { ...turn, context: { lastUnansweredText: undefined, ...(turn.context || {}) } };
      if (escalate) turn = ensureSupportButton(withLeadIn(turn, escalationAck(circling)));
      else if (calmOnce) {
        const deflected = preset.id === CONTACT_SUPPORT_PRESET_ID && (turn.context?.supportAsks ?? 0) > (before.supportAsks ?? 0);
        turn = deflected
          ? { ...turn, messages: [calmingDeflect()], quickButtons: (turn.quickButtons || []).map((b) => (b.label === 'I still need support' ? { ...b, label: 'Get support on this now' } : b)) }
          : withLeadIn(turn, calmingAck(user.firstName));
      }
      const result = await applyTurn(withContext(turn, mood), preset.id);
      return finish(preset.id === END_CHAT_PRESET_ID ? { ...result, endChat: true } : result);
    }

    if (escalate) {
      return finish(await applyTurn(withContext(escalationOfferTurn(presets, hasCase, circling), mood)));
    }

    const llmTurn = await askLlm(trimmed, sentiment.frustrated);
    if (llmTurn) {
      const turn = calmOnce ? withLeadIn(llmTurn, calmingAck(user.firstName)) : llmTurn;
      return finish(await applyTurn(withContext(turn, mood), LLM_INTENT_ID));
    }

    // Nothing could answer: offer the design team (case in context) or a support call-back, and keep
    // the question so the ticket can carry it. One-word acknowledgements are not worth a ticket.
    const worthKeeping = !THROWAWAY_INPUT.test(trimmed);
    let turn = withContext(unresolvedTurn(presets, hasCase), {
      pendingIntentId: undefined,
      contactStep: undefined,
      contactDraft: undefined,
      ...(worthKeeping ? { lastUnansweredText: trimmed } : {}),
      ...mood,
    });
    if (calmOnce) turn = withLeadIn(turn, calmingAck(user.firstName));
    return finish(await applyTurn(turn));
  };

  return {
    async start() {
      const session = await conversation.getOrCreate();
      if (session.messages.length === 0) {
        await greet();
      }
      const current = conversation.getSession();
      return { messages: [...current.messages], quickButtons: lastQuickButtons(current.messages) };
    },

    async handleUserText(text) {
      const result = await handleUserText(text);
      if (result.newMessages.length > 0) scheduleTicketSync();
      return result;
    },

    async handleQuickButton(value) {
      const result = await handleQuickButtonTurn(value);
      if (result.newMessages.length > 0) scheduleTicketSync();
      return result;
    },

    async newSession(feedback) {
      await closeTicket(feedback);
      await conversation.endSession(feedback);
      await greet();
      const current = conversation.getSession();
      return { messages: [...current.messages], quickButtons: lastQuickButtons(current.messages) };
    },

    async rateMessage(messageId, rating) {
      await conversation.setMessageFeedback(messageId, rating);
      // Thumbs are part of the transcript on the ticket, so push them even though no message was added.
      scheduleTicketSync(true);
    },

    quickButtonText,

    flushTicket: () => ticketQueue,
  };

  async function handleQuickButtonTurn(value: string): Promise<EngineResult> {
    const raw = String(value ?? '');

    if (raw.startsWith(CASE_BUTTON_PREFIX)) {
      const refNo = raw.slice(CASE_BUTTON_PREFIX.length);
      const preset = presets.find((p) => p.id === CASE_LOOKUP_PRESET_ID);
      const userMessage = await conversation.append({ role: 'user', text: `Case ${refNo}`, intentId: preset?.id, caseRefNo: refNo });
      await conversation.setContext(CLEAR_CONTACT_FLOW);
      const turn = preset ? await runPreset(preset, { text: refNo, caseRefNo: refNo }) : fallbackTurn(personality(), presets);
      const result = await applyTurn(turn, preset?.id);
      return { ...result, newMessages: [userMessage, ...result.newMessages] };
    }

    const presetId = raw.startsWith(PRESET_BUTTON_PREFIX) ? raw.slice(PRESET_BUTTON_PREFIX.length) : raw;
    const preset = presets.find((p) => p.id === presetId);
    if (!preset) return handleUserText(raw);

    const userMessage = await conversation.append({ role: 'user', text: preset.label, intentId: preset.id });
    // A contact-flow button keeps the flow's state; any other button abandons it.
    await conversation.setContext(preset.id === CONTACT_SUPPORT_PRESET_ID ? { pendingIntentId: undefined } : CLEAR_CONTACT_FLOW);
    const turn = await runPreset(preset, { text: preset.label });
    const result = await applyTurn(turn, preset.id);
    return { ...result, newMessages: [userMessage, ...result.newMessages], ...(preset.id === END_CHAT_PRESET_ID ? { endChat: true } : {}) };
  }
}
